import { ScreenApiDefinition, validateScreenApis } from '@shared-lib/foundry-services/common-api/screen-api.model';
import { upgradeLegacyBarNode } from '@shared-lib/chart-components/unified-bar-chart/legacy-bar-migration';
import { computed, signal } from '@angular/core';
import { isConfigObject, isJsonValue, setConfigPath } from './config-editor/config-value.utils';
import type { FeatureDefinition, LayoutItem } from '../../screen-renderer/layout/layout.model';

/** Internal editor state compiled to and from the shared screen JSON format. */
export type ObjectKind = 'frame' | 'text' | 'rectangle' | 'ellipse' | 'line' | 'button' | 'stat' | 'table' | 'bar' | 'donut' | 'input' | 'component';
export interface PaletteItem { kind: ObjectKind; label: string; icon: string; group: string; width: number; height: number; }
export const PALETTE: readonly PaletteItem[] = [
  { kind: 'frame', label: 'Frame', icon: 'web_asset', group: 'Layout', width: 420, height: 300 },
  { kind: 'text', label: 'Text', icon: 'title', group: 'Basics', width: 280, height: 52 },
  { kind: 'rectangle', label: 'Rectangle', icon: 'crop_square', group: 'Basics', width: 180, height: 120 },
  { kind: 'ellipse', label: 'Ellipse', icon: 'circle', group: 'Basics', width: 120, height: 120 },
  { kind: 'line', label: 'Divider', icon: 'horizontal_rule', group: 'Basics', width: 280, height: 16 },
  { kind: 'button', label: 'Button', icon: 'smart_button', group: 'Controls', width: 160, height: 44 },
  { kind: 'input', label: 'Input', icon: 'input', group: 'Controls', width: 260, height: 76 },
  { kind: 'stat', label: 'Stat card', icon: 'pin', group: 'Components', width: 240, height: 130 },
  { kind: 'table', label: 'Table', icon: 'table_chart', group: 'Components', width: 660, height: 250 },
  { kind: 'bar', label: 'Bar chart', icon: 'bar_chart', group: 'Components', width: 510, height: 260 },
  { kind: 'donut', label: 'Donut chart', icon: 'donut_large', group: 'Components', width: 320, height: 260 },
];
export interface EditorObject {
  id: string; kind: ObjectKind; name: string; x: number; y: number; width: number; height: number;
  text: string; value: string; fill: string; color: string; border: string; radius: number;
  fontSize: number; bold: boolean; opacity: number; hidden: boolean; locked: boolean;
  componentId?: string;
  config?: Record<string, unknown>;
  configKey?: string;
}
/** Read-only compatibility for saved drafts from before the shared renderer. */
export interface LegacySourceScreen {
  screenId: string;
  config: Record<string, unknown>;
  /** Removed from this editable copy. Native settings remain for undo and source visibility guards. */
  removedComponents?: string[];
}
export type ObjectBounds = Pick<EditorObject, 'x' | 'y' | 'width' | 'height'>;
export interface EditorPage { id: string; name: string; width: number; height: number; background: string; nodes: EditorObject[]; source?: LegacySourceScreen; layout?: LayoutItem; }
export interface EditorDocument { version: 1; name: string; activePageId: string; pages: EditorPage[]; apis?: ScreenApiDefinition[]; features?: FeatureDefinition[]; screen?:{slug?:string;title?:string;accessKey?:string;icon?:string}; breakpoints?:{mobile:number;tablet:number}; shell?: { sideNavbar?: Record<string, unknown>; backgroundEffects?: boolean; contained?: boolean }; }
const uid = () => crypto.randomUUID();
const clone = <T>(value: T): T => structuredClone(value);
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const createPage = (name: string): EditorPage => ({ id: uid(), name, width: 1120, height: 820, background: '#f7f8fa', nodes: [] });

export class DesignEditorState {
  private readonly initial = createPage('Overview');
  readonly document = signal<EditorDocument>({ version: 1, name: 'Untitled design', activePageId: this.initial.id, pages: [this.initial] });
  readonly selectedId = signal<string | null>(null);
  readonly activePage = computed(() => this.document().pages.find(page => page.id === this.document().activePageId)!);
  readonly selected = computed(() => this.activePage().nodes.find(node => node.id === this.selectedId()) ?? null);
  private readonly past = signal<{ document: EditorDocument; selectedId: string | null }[]>([]);
  private readonly future = signal<{ document: EditorDocument; selectedId: string | null }[]>([]);
  readonly canUndo = computed(() => this.past().length > 0);
  readonly canRedo = computed(() => this.future().length > 0);

  private remember(): void {
    this.past.update(history => [...history.slice(-79), { document: clone(this.document()), selectedId: this.selectedId() }]);
    this.future.set([]);
  }
  private updatePage(update: (page: EditorPage) => EditorPage): void {
    this.document.update(doc => ({ ...doc, pages: doc.pages.map(page => {
      if (page.id !== doc.activePageId) return page;
      const next = update(page);
      if (next.layout === undefined) { const { layout, ...canvas } = next; return canvas; }
      return next;
    }) }));
  }
  select(id: string | null): void { this.selectedId.set(id); }
  setPage(id: string): void {
    if (!this.document().pages.some(page => page.id === id)) return;
    this.document.update(doc => ({ ...doc, activePageId: id }));
    this.select(null);
  }
  addPage(): void {
    this.remember();
    const page = createPage(`Screen ${this.document().pages.length + 1}`);
    this.document.update(doc => ({ ...doc, activePageId: page.id, pages: [...doc.pages, page] }));
    this.select(null);
  }
  addComponent(componentId: string, name: string, config: Record<string, unknown>, width = 520, height = 320, placement?: ObjectBounds): EditorObject {
    const base = this.add('frame');
    const node = this.bound({ ...base, kind: 'component', componentId, name, config: clone(config), width, height, ...placement }, this.activePage());
    const size = (item:LayoutItem):LayoutItem => item.nodeId===node.id ? {...item,styles:{...item.styles,left:`${node.x}px`,top:`${node.y}px`,width:`${node.width}px`,height:`${node.height}px`}} : {...item,...(item.children?{children:item.children.map(size)}:{})};
    this.updatePage(page => ({ ...page, nodes: page.nodes.map(item => item.id === base.id ? node : item), ...(page.layout?{layout:size(page.layout)}:{}) }));
    return node;
  }
  patchComponentConfig(path: readonly string[], value: unknown): void {
    const node = this.selected();
    if (!node || node.kind !== 'component' || !isJsonValue(value)) return;
    const config = setConfigPath(node.config ?? {}, path, value);
    if (isConfigObject(config)) this.patchSelected({ config });
  }
  patchFeatureConfig(id: string, path: readonly string[], value: unknown): void {
    if (!isJsonValue(value)) return;
    this.remember();
    this.document.update(doc => ({ ...doc, features: doc.features?.map(feature => feature.id === id ? { ...feature, config: setConfigPath(feature.config, path, value) as Record<string, unknown> } : feature) }));
  }
  patchLayout(nodeId: string, styles: Record<string, string | number>): void {
    this.remember();
    const update = (item: LayoutItem): LayoutItem => item.nodeId === nodeId ? { ...item, styles } : { ...item, ...(item.children?{children:item.children.map(update)}:{}) };
    this.updatePage(page => ({ ...page, layout: page.layout ? update(page.layout) : undefined }));
  }
  removePage(): void {
    if (this.document().pages.length === 1) return;
    this.remember();
    this.document.update(doc => {
      const pages = doc.pages.filter(page => page.id !== doc.activePageId);
      return { ...doc, pages, activePageId: pages[0].id };
    });
    this.select(null);
  }
  rename(name: string): void {
    if (!name.trim() || name.trim() === this.document().name) return;
    this.remember();
    this.document.update(doc => ({ ...doc, name: name.trim().slice(0, 100) }));
  }
  renamePage(name: string): void {
    if (!name.trim() || name.trim() === this.activePage().name) return;
    this.remember();
    this.updatePage(page => ({ ...page, name: name.trim().slice(0, 80) }));
  }
  patchPage(patch: Partial<Pick<EditorPage, 'width' | 'height' | 'background' | 'layout'>>): void {
    this.remember();
    this.updatePage(page => {
      const updated = { ...page, ...patch };
      updated.width = clamp(Number.isFinite(updated.width) ? updated.width : page.width, 320, 2400);
      updated.height = clamp(Number.isFinite(updated.height) ? updated.height : page.height, 320, 3000);
      return { ...updated, nodes: updated.nodes.map(node => this.bound(node, updated)) };
    });
  }
  add(kind: ObjectKind, position?: { x: number; y: number }): EditorObject {
    this.remember();
    const item = PALETTE.find(item => item.kind === kind)!;
    const node: EditorObject = this.bound({
      id: uid(), kind, name: item.label, x: position?.x ?? 40, y: position?.y ?? 40,
      width: item.width, height: item.height,
      text: kind === 'text' ? 'Your heading here' : kind === 'button' ? 'Continue' : kind === 'input' ? 'Field label' : item.label,
      value: kind === 'stat' ? '24,580' : '',
      fill: kind === 'button' ? '#043d63' : kind === 'rectangle' || kind === 'ellipse' ? '#dceafb' : '#ffffff',
      color: kind === 'button' ? '#ffffff' : '#172b40', border: '#e3e7ed', radius: kind === 'ellipse' ? 100 : 8,
      fontSize: kind === 'text' ? 28 : 14, bold: kind === 'text', opacity: 100, hidden: false, locked: false,
    }, this.activePage());
    this.updatePage(page => ({ ...page, nodes: [...page.nodes, node], layout: page.layout ? { ...page.layout, children: [...(page.layout.children ?? []), { id: `placement-${node.id}`, nodeId: node.id, styles: { position: 'absolute', left: `${node.x}px`, top: `${node.y}px`, width: `${node.width}px`, height: `${node.height}px` } }] } : undefined }));
    this.select(node.id);
    return node;
  }
  private bound(node: EditorObject, page: EditorPage): EditorObject {
    const width = clamp(Number.isFinite(node.width) ? node.width : 100, 16, page.width);
    const height = clamp(Number.isFinite(node.height) ? node.height : 100, 16, page.height);
    return { ...node, width, height,
      x: clamp(Number.isFinite(node.x) ? node.x : 0, 0, page.width - width),
      y: clamp(Number.isFinite(node.y) ? node.y : 0, 0, page.height - height),
      radius: clamp(Number.isFinite(node.radius) ? node.radius : 8, 0, 200),
      fontSize: clamp(Number.isFinite(node.fontSize) ? node.fontSize : 14, 8, 120),
      opacity: clamp(Number.isFinite(node.opacity) ? node.opacity : 100, 0, 100),
    };
  }
  patchSelected(patch: Partial<EditorObject>, record = true): void {
    const selected = this.selected();
    if (!selected) return;
    if (selected.locked && Object.keys(patch).some(key => !['locked', 'hidden'].includes(key))) return;
    if (record) this.remember();
    this.updatePage(page => {
      const placed = this.bound({ ...selected, ...patch, id: selected.id, kind: selected.kind }, page);
      const placement = (item: LayoutItem): LayoutItem => item.nodeId === selected.id ? { ...item, styles: { ...item.styles,
        ...('x' in patch ? {left:`${placed.x}px`}:{}), ...('y' in patch?{top:`${placed.y}px`}:{}), ...('width' in patch?{width:`${placed.width}px`}:{}), ...('height' in patch?{height:`${placed.height}px`}:{}) } } : { ...item, ...(item.children?{children:item.children.map(placement)}:{}) };
      return { ...page, nodes:page.nodes.map(node=>node.id===selected.id?placed:node), layout:page.layout?placement(page.layout):undefined };
    });
  }
  beginGesture(): void { if (this.selected() && !this.selected()!.locked) this.remember(); }
  duplicateSelected(): void {
    const node = this.selected();
    if (!node || node.locked) return;
    this.remember();
    const duplicate = this.bound({ ...clone(node), id: uid(), name: `${node.name} copy`, x: node.x + 24, y: node.y + 24 }, this.activePage());
    const duplicatePlacement = (item:LayoutItem):LayoutItem => {
      if(!item.children)return item;
      return {...item,children:item.children.flatMap(child=>child.nodeId===node.id?[child,{...clone(child),id:`placement-${duplicate.id}`,nodeId:duplicate.id}]:[duplicatePlacement(child)])};
    };
    this.updatePage(page => ({ ...page, nodes: [...page.nodes, duplicate], ...(page.layout?{layout:duplicatePlacement(page.layout)}:{}) }));
    this.select(duplicate.id);
  }
  deleteSelected(): void {
    if (!this.selected() || this.selected()!.locked) return;
    this.remember();
    const prune = (item: LayoutItem): LayoutItem => ({ ...item, ...(item.children?{children:item.children.filter(child=>child.nodeId!==this.selectedId()).map(prune)}:{}) });
    this.updatePage(page => ({ ...page, nodes: page.nodes.filter(node => node.id !== this.selectedId()), layout:page.layout?prune(page.layout):undefined }));
    this.select(null);
  }
  reorder(direction: -1 | 1): void {
    if (!this.selected() || this.selected()!.locked) return;
    if(this.activePage().layout){
      let moved=false;
      const arrange=(item:LayoutItem):LayoutItem=>{
        if(!item.children)return item;
        const children=item.children.map(arrange);const index=children.findIndex(child=>child.nodeId===this.selectedId());const target=index+direction;
        if(index>=0&&target>=0&&target<children.length){[children[index],children[target]]=[children[target],children[index]];moved=true;}
        return {...item,children};
      };
      const layout=arrange(this.activePage().layout!);if(!moved)return;this.remember();this.updatePage(page=>({...page,layout}));return;
    }
    const index = this.activePage().nodes.findIndex(node => node.id === this.selectedId());
    const target = index + direction;
    if (target < 0 || target >= this.activePage().nodes.length) return;
    this.remember();
    this.updatePage(page => {
      const nodes = [...page.nodes];
      [nodes[index], nodes[target]] = [nodes[target], nodes[index]];
      return { ...page, nodes };
    });
  }
  undo(): void {
    const previous = this.past().at(-1);
    if (!previous) return;
    this.future.update(history => [...history, { document: clone(this.document()), selectedId: this.selectedId() }]);
    this.past.update(history => history.slice(0, -1));
    this.document.set(previous.document);
    this.selectedId.set(previous.selectedId);
  }
  redo(): void {
    const next = this.future().at(-1);
    if (!next) return;
    this.past.update(history => [...history, { document: clone(this.document()), selectedId: this.selectedId() }]);
    this.future.update(history => history.slice(0, -1));
    this.document.set(next.document);
    this.selectedId.set(next.selectedId);
  }
  serialize(): string { return JSON.stringify(this.document()); }
  replaceDocument(document:EditorDocument,record=true):void {
    if(record)this.remember();this.document.set({...document,pages:document.pages.map(page=>({...page,nodes:page.nodes.map(node=>upgradeLegacyBarNode(node))}))});
    if(!document.pages.some(page=>page.nodes.some(node=>node.id===this.selectedId())))this.select(null);
  }
  patchPlacement(id:string,styles:Record<string,string|number>,size:'desktop'|'tablet'|'mobile'='desktop',record=true):void {
    if(record)this.remember();
    const merge=(base:Record<string,string|number>={})=>{const result={...base,...styles};if('left' in styles||'top' in styles)delete result['inset'];return result;};
    const update=(item:LayoutItem):LayoutItem=>item.id===id||item.nodeId===id?
      {...item,...(size==='desktop'?{styles:merge(item.styles)}:{responsive:{...item.responsive,[size]:merge(item.responsive?.[size])}})}:
      {...item,...(item.children?{children:item.children.map(update)}:{})};
    this.updatePage(page=>({...page,...(page.layout?{layout:update(page.layout)}:{})}));
  }
  restore(json: string): boolean {
    try {
      const doc = JSON.parse(json) as EditorDocument;
      const color = (value: unknown) => typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);
      const short = (value: unknown) => typeof value === 'string' && value.length <= 10000;
      if (doc.version !== 1 || !short(doc.name) || !Array.isArray(doc.pages) || !doc.pages.length || doc.pages.length > 100) return false;
      if (doc.apis !== undefined) validateScreenApis(doc.apis);
      const ids = new Set<string>();
      for (const page of doc.pages) {
        if (!short(page.id) || ids.has(page.id) || !short(page.name) || !color(page.background) || !Number.isFinite(page.width) || page.width < 320 || page.width > 2400 || !Number.isFinite(page.height) || page.height < 320 || page.height > 3000 || !Array.isArray(page.nodes) || page.nodes.length > 2000) return false;
        ids.add(page.id);
        if (page.source && (!short(page.source.screenId) || !isConfigObject(page.source.config) || !isJsonValue(page.source.config))) return false;
        if (page.source?.removedComponents !== undefined && (!Array.isArray(page.source.removedComponents) || !page.source.removedComponents.every(key => short(key) && isConfigObject(page.source!.config['components']) && Object.hasOwn(page.source!.config['components'], key)))) return false;
        for (const node of page.nodes) {
          if (!short(node.id) || ids.has(node.id) || !(node.kind === 'component' || PALETTE.some(item => item.kind === node.kind)) || ![node.name, node.text, node.value].every(short) || ![node.fill, node.color, node.border].every(color) || ![node.x, node.y, node.width, node.height, node.radius, node.fontSize, node.opacity].every(Number.isFinite) || ![node.hidden, node.locked, node.bold].every(value => typeof value === 'boolean')) return false;
          if (node.kind === 'component' && (!short(node.componentId) || !isConfigObject(node.config) || !isJsonValue(node.config))) return false;
          ids.add(node.id);
        }
        page.nodes = page.nodes.map(node => this.bound(upgradeLegacyBarNode(node), page));
      }
      if (!doc.pages.some(page => page.id === doc.activePageId)) return false;
      this.document.set(doc);
      this.past.set([]); this.future.set([]); this.select(null);
      return true;
    } catch { return false; }
  }
  /** Editable starter composition; sample values never invoke Foundry APIs. */
  starter(): void {
    this.rename('Operations overview');
    const place = (kind: ObjectKind, patch: Partial<EditorObject>) => { this.add(kind); this.patchSelected(patch); };
    place('text', { x: 32, y: 28, width: 650, text: 'Operations overview', fontSize: 28 });
    place('text', { x: 32, y: 78, width: 800, height: 28, text: 'A clear view of your business. Make this screen your own.', fontSize: 14, color: '#627086', bold: false });
    place('stat', { x: 32, y: 134, width: 336, text: 'Total revenue', value: '₹24.58L' });
    place('stat', { x: 392, y: 134, width: 336, text: 'Active customers', value: '1,248' });
    place('stat', { x: 752, y: 134, width: 336, text: 'Approval rate', value: '94.2%' });
    place('bar', { x: 32, y: 288, width: 672, height: 256, text: 'Revenue by month' });
    place('donut', { x: 728, y: 288, width: 360, height: 256, text: 'Sales by region' });
    place('table', { x: 32, y: 568, width: 1056, height: 220, text: 'Recent activity' });
    this.past.set([]); this.future.set([]); this.select(null);
  }
}
